import { CreditCard, Calendar, Users, FileText, TrendingUp, Clock, LayoutDashboard, Banknote, Wallet, ClipboardList, CheckCircle2, AlertCircle, ArrowRight, Timer } from "lucide-react";
import { Link } from "react-router-dom";
import { StatCard } from "../../../components/ui/StatCard";
import { useAuth } from "../../../hooks/useAuth";
import { useAdminSummary, useBusinessSummary } from "../../../hooks/useSummary";

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function AdminDashboard() {
  const { user, isAdmin, hasPageAccess, activeBusinessId, businesses } = useAuth();
  const globalSummary = useAdminSummary(isAdmin && !activeBusinessId);
  const bizSummary = useBusinessSummary(activeBusinessId);
  const { data: summary, isLoading } = activeBusinessId ? bizSummary : globalSummary;

  const firstName = user?.firstName ?? user?.username ?? "Admin";
  const activeBizName = businesses.find((b) => b.businessId === activeBusinessId)?.name;

  const pendingLoans      = summary?.loans.pendingCount ?? 0;
  const approvedLoans     = summary?.loans.approvedCount ?? 0;
  const loanActionCount   = pendingLoans + approvedLoans;
  const pendingLeaves     = summary?.leaves.pendingCount ?? 0;
  const totalEmployees    = summary?.employees.total ?? 0;
  const activeEmployees   = summary?.employees.active ?? 0;
  const inactiveEmployees = summary?.employees.inactive ?? 0;
  const draftPayslips     = summary?.payslip.draftCount ?? 0;
  const finalizedPayslips = summary?.payslip.finalizedCount ?? 0;
  const periodLabel       = summary?.payslip.currentPeriodLabel ?? "—";
  const pendingCashAdvance  = summary?.cashAdvance.pendingCount ?? 0;
  const approvedCashAdvance = summary?.cashAdvance.approvedCount ?? 0;
  const cashAdvActionCount  = pendingCashAdvance + approvedCashAdvance;
  const pendingEncashments  = summary?.wallet.pendingEncashmentCount ?? 0;
  const approvedEncashments = summary?.wallet.approvedEncashmentCount ?? 0;
  const encashmentActionCount = pendingEncashments + approvedEncashments;
  const pendingWalletAdv    = summary?.wallet.pendingAdvanceCount ?? 0;
  const approvedWalletAdv   = summary?.wallet.approvedAdvanceCount ?? 0;
  const walletAdvActionCount = pendingWalletAdv + approvedWalletAdv;
  const openTasks           = summary?.tasks.openCount ?? 0;
  const pendingWorkAdj      = summary?.workAdjustments?.pendingCount ?? 0;

  const showLoans       = hasPageAccess("admin:loans");
  const showLeave       = hasPageAccess("admin:leave");
  const showUsers       = hasPageAccess("admin:users");
  const showPayslip     = hasPageAccess("admin:payslip");
  const showCashAdvance = hasPageAccess("admin:cash-advance");
  const showWallet      = hasPageAccess("admin:wallet");
  const showTasks       = hasPageAccess("admin:tasks");
  const showWorkAdj     = hasPageAccess("admin:work-adjustments");

  const hasAnyCard = showLoans || showLeave || showUsers || showPayslip || showCashAdvance || showWallet || showTasks || showWorkAdj;

  // Total items requiring action across everything the user can see
  const totalPending = (showLoans ? loanActionCount : 0)
    + (showLeave ? pendingLeaves : 0)
    + (showCashAdvance ? cashAdvActionCount : 0)
    + (showWallet ? encashmentActionCount + walletAdvActionCount : 0)
    + (showWorkAdj ? pendingWorkAdj : 0);

  // Helpers for urgency styling
  const urgentCard = (count: number) =>
    count > 0
      ? { className: "border-l-4 border-l-[#2447d9] bg-[#fafbff]", valueClassName: "text-[#2447d9]" }
      : { className: "", valueClassName: "text-[#94a3b8]" };

  const linkLabel = (count: number, noun: string) =>
    count > 0 ? `View ${count} ${noun}` : "View All";

  // Payroll progress
  const payrollTotal = totalEmployees;
  const payrollPct = payrollTotal > 0 ? Math.round((finalizedPayslips / payrollTotal) * 100) : 0;

  return (
    <div className="space-y-6">

      {/* ── Greeting / attention bar ─────────────────────────────────────── */}
      <div className="rounded-2xl border border-[#e2e8f0] bg-white px-6 py-4 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-medium text-[#94a3b8] uppercase tracking-wide mb-0.5">
              {greeting()}{activeBizName ? ` · ${activeBizName}` : ""}
            </p>
            <h1 className="text-xl font-bold text-[#0f172a]">{firstName}</h1>
          </div>

          {!isLoading && hasAnyCard && (
            <div className={`flex items-center gap-2 rounded-xl px-4 py-2.5 ${
              totalPending > 0
                ? "bg-[#fef2f2] border border-[#fecaca]"
                : "bg-[#f0fdf4] border border-[#bbf7d0]"
            }`}>
              {totalPending > 0 ? (
                <>
                  <AlertCircle className="h-4 w-4 text-[#dc2626] shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[#dc2626]">
                      {totalPending} item{totalPending !== 1 ? "s" : ""} need{totalPending === 1 ? "s" : ""} your attention
                    </p>
                    <p className="text-xs text-[#f87171]">
                      {[
                        showLoans && loanActionCount > 0 && `${loanActionCount} loan${loanActionCount !== 1 ? "s" : ""}`,
                        showLeave && pendingLeaves > 0 && `${pendingLeaves} leave`,
                        showCashAdvance && cashAdvActionCount > 0 && `${cashAdvActionCount} cash advance`,
                        showWallet && encashmentActionCount > 0 && `${encashmentActionCount} encashment`,
                        showWallet && walletAdvActionCount > 0 && `${walletAdvActionCount} wallet advance`,
                        showWorkAdj && pendingWorkAdj > 0 && `${pendingWorkAdj} work time claim`,
                      ].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 text-[#16a34a] shrink-0" />
                  <p className="text-sm font-semibold text-[#16a34a]">All clear — nothing pending</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {hasAnyCard ? (
        <>
          {/* ── Stat cards ──────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {showLoans && (
              <StatCard
                title="Loans"
                value={String(loanActionCount)}
                subtitle={
                  loanActionCount === 0 ? "No action required"
                  : [
                      pendingLoans > 0 && `${pendingLoans} pending approval`,
                      approvedLoans > 0 && `${approvedLoans} awaiting release`,
                    ].filter(Boolean).join(" · ")
                }
                icon={<CreditCard className="h-6 w-6" />}
                iconBgClass={loanActionCount > 0 ? "bg-[#eff3ff] text-[#2447d9]" : "bg-[#f1f5f9] text-[#94a3b8]"}
                viewAllHref="/admin/loans"
                linkLabel={linkLabel(loanActionCount, "to action")}
                loading={isLoading}
                {...urgentCard(loanActionCount)}
              />
            )}
            {showLeave && (
              <StatCard
                title="Pending Leave"
                value={String(pendingLeaves)}
                subtitle={pendingLeaves > 0 ? `${pendingLeaves} awaiting approval` : "None pending"}
                icon={<Calendar className="h-6 w-6" />}
                iconBgClass={pendingLeaves > 0 ? "bg-[#f0fdf4] text-[#16a34a]" : "bg-[#f1f5f9] text-[#94a3b8]"}
                viewAllHref="/admin/leave"
                linkLabel={linkLabel(pendingLeaves, "pending")}
                loading={isLoading}
                {...urgentCard(pendingLeaves)}
              />
            )}
            {showUsers && (
              <StatCard
                title="Active Employees"
                value={String(activeEmployees)}
                subtitle={inactiveEmployees > 0 ? `${inactiveEmployees} inactive · ${totalEmployees} total` : `${totalEmployees} total`}
                icon={<Users className="h-6 w-6" />}
                iconBgClass="bg-[#f5f3ff] text-[#7c3aed]"
                viewAllHref="/admin/users"
                loading={isLoading}
              />
            )}
            {showPayslip && (
              <StatCard
                title="Payslip Period"
                value={summary?.payslip.currentPeriod.split("-").slice(1).join(" ") ?? "—"}
                subtitle={
                  isLoading ? undefined
                    : draftPayslips > 0 ? `${draftPayslips} draft · ${finalizedPayslips} finalized`
                    : finalizedPayslips > 0 ? `${finalizedPayslips} finalized`
                    : "No payslips this period"
                }
                icon={<FileText className="h-6 w-6" />}
                iconBgClass={draftPayslips > 0 ? "bg-[#fffbeb] text-[#d97706]" : "bg-[#f0fdf4] text-[#16a34a]"}
                viewAllHref="/admin/payslip"
                loading={isLoading}
                valueClassName={draftPayslips > 0 ? "text-[#d97706]" : "text-[#0f172a]"}
              />
            )}
            {showCashAdvance && (
              <StatCard
                title="Cash Advances"
                value={String(cashAdvActionCount)}
                subtitle={
                  cashAdvActionCount === 0 ? "No action required"
                  : [
                      pendingCashAdvance > 0 && `${pendingCashAdvance} pending approval`,
                      approvedCashAdvance > 0 && `${approvedCashAdvance} awaiting release`,
                    ].filter(Boolean).join(" · ")
                }
                icon={<Banknote className="h-6 w-6" />}
                iconBgClass={cashAdvActionCount > 0 ? "bg-[#f0fdf4] text-[#16a34a]" : "bg-[#f1f5f9] text-[#94a3b8]"}
                viewAllHref="/admin/cash-advance"
                linkLabel={linkLabel(cashAdvActionCount, "to action")}
                loading={isLoading}
                {...urgentCard(cashAdvActionCount)}
              />
            )}
            {showWallet && (
              <StatCard
                title="Encashments"
                value={String(encashmentActionCount)}
                subtitle={
                  encashmentActionCount === 0 ? "No action required"
                  : [
                      pendingEncashments > 0 && `${pendingEncashments} pending approval`,
                      approvedEncashments > 0 && `${approvedEncashments} awaiting release`,
                    ].filter(Boolean).join(" · ")
                }
                icon={<Wallet className="h-6 w-6" />}
                iconBgClass={encashmentActionCount > 0 ? "bg-[#f5f3ff] text-[#7c3aed]" : "bg-[#f1f5f9] text-[#94a3b8]"}
                viewAllHref="/admin/wallet"
                linkLabel={linkLabel(encashmentActionCount, "to action")}
                loading={isLoading}
                {...urgentCard(encashmentActionCount)}
              />
            )}
            {showWallet && (
              <StatCard
                title="Wallet Advances"
                value={String(walletAdvActionCount)}
                subtitle={
                  walletAdvActionCount === 0 ? "No action required"
                  : [
                      pendingWalletAdv > 0 && `${pendingWalletAdv} pending approval`,
                      approvedWalletAdv > 0 && `${approvedWalletAdv} awaiting release`,
                    ].filter(Boolean).join(" · ")
                }
                icon={<Wallet className="h-6 w-6" />}
                iconBgClass={walletAdvActionCount > 0 ? "bg-[#fef9c3] text-[#a16207]" : "bg-[#f1f5f9] text-[#94a3b8]"}
                viewAllHref="/admin/wallet"
                linkLabel={linkLabel(walletAdvActionCount, "to action")}
                loading={isLoading}
                {...(walletAdvActionCount > 0 ? { className: "border-l-4 border-l-[#ca8a04] bg-[#fffef0]", valueClassName: "text-[#a16207]" } : {})}
              />
            )}
            {showTasks && (
              <StatCard
                title="Open Tasks"
                value={String(openTasks)}
                subtitle={openTasks > 0 ? `${openTasks} in progress or todo` : "No open tasks"}
                icon={<ClipboardList className="h-6 w-6" />}
                iconBgClass={openTasks > 0 ? "bg-[#fff7ed] text-[#ea580c]" : "bg-[#f1f5f9] text-[#94a3b8]"}
                viewAllHref="/admin/tasks"
                linkLabel={linkLabel(openTasks, "open")}
                loading={isLoading}
                {...(openTasks > 0 ? { className: "border-l-4 border-l-[#ea580c] bg-[#fffbf7]", valueClassName: "text-[#ea580c]" } : {})}
              />
            )}
            {showWorkAdj && (
              <StatCard
                title="Work Time Claims"
                value={String(pendingWorkAdj)}
                subtitle={pendingWorkAdj > 0 ? `${pendingWorkAdj} pending review` : "No pending claims"}
                icon={<Timer className="h-6 w-6" />}
                iconBgClass={pendingWorkAdj > 0 ? "bg-[#eff3ff] text-[#2447d9]" : "bg-[#f1f5f9] text-[#94a3b8]"}
                viewAllHref="/admin/work-adjustments"
                linkLabel={linkLabel(pendingWorkAdj, "pending")}
                loading={isLoading}
                {...urgentCard(pendingWorkAdj)}
              />
            )}
          </div>

          {/* ── Payroll summary — full width ─────────────────────────────── */}
          {showPayslip && (
            <div className="rounded-xl border border-[#e2e8f0] bg-white shadow-sm overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#f1f5f9]">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#fffbeb]">
                    <Clock className="h-4 w-4 text-[#d97706]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0f172a]">Payroll — {periodLabel}</p>
                    <p className="text-xs text-[#94a3b8]">Current payslip period</p>
                  </div>
                </div>
                <Link
                  to="/admin/payslip"
                  className="flex items-center gap-1 text-xs font-medium text-[#2447d9] hover:text-[#1e3a8a] transition-colors"
                >
                  Process Payslip <ArrowRight className="h-3 w-3" />
                </Link>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-[#f1f5f9]">
                {[
                  { label: "Total Employees", value: totalEmployees, color: "text-[#0f172a]" },
                  { label: "Active",           value: activeEmployees, color: "text-[#16a34a]" },
                  { label: "Finalized",        value: finalizedPayslips, color: "text-[#2447d9]" },
                  { label: "Draft",            value: draftPayslips,    color: draftPayslips > 0 ? "text-[#d97706]" : "text-[#94a3b8]" },
                ].map((stat) => (
                  <div key={stat.label} className="px-5 py-4">
                    <p className="text-xs text-[#94a3b8] mb-1">{stat.label}</p>
                    {isLoading ? (
                      <div className="h-6 w-10 rounded bg-[#f1f5f9] animate-pulse" />
                    ) : (
                      <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Progress bar + alerts */}
              {!isLoading && payrollTotal > 0 && (
                <div className="px-5 py-3 border-t border-[#f1f5f9] space-y-2.5 bg-[#f8fafc]">
                  {/* Progress */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 rounded-full bg-[#e2e8f0] overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all ${draftPayslips > 0 ? "bg-[#2447d9]" : "bg-[#16a34a]"}`}
                        style={{ width: `${payrollPct}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-[#64748b] shrink-0">
                      {finalizedPayslips} / {payrollTotal} finalized ({payrollPct}%)
                    </span>
                  </div>

                  {/* Contextual alerts */}
                  {draftPayslips > 0 && (
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-3.5 w-3.5 text-[#d97706] shrink-0" />
                      <p className="text-xs text-[#d97706]">
                        <span className="font-semibold">{draftPayslips} payslip{draftPayslips > 1 ? "s" : ""}</span> still in draft — finalize before the period ends
                      </p>
                    </div>
                  )}
                  {draftPayslips === 0 && finalizedPayslips > 0 && (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-[#16a34a] shrink-0" />
                      <p className="text-xs text-[#16a34a] font-medium">All payslips finalized for this period</p>
                    </div>
                  )}
                  {(showLoans && loanActionCount > 0) || (showLeave && pendingLeaves > 0) ? (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-3.5 w-3.5 text-[#dc2626] shrink-0" />
                      <p className="text-xs text-[#dc2626]">
                        <span className="font-semibold">{(showLoans ? loanActionCount : 0) + pendingLeaves} item{((showLoans ? loanActionCount : 0) + pendingLeaves) > 1 ? "s" : ""}</span> pending — may affect payroll
                      </p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#e2e8f0] bg-[#f8fafc] py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#eff3ff] mb-4">
            <LayoutDashboard className="h-7 w-7 text-[#2447d9]" />
          </div>
          <p className="text-base font-semibold text-[#0f172a]">Nothing to show here</p>
          <p className="mt-1 text-sm text-[#64748b] max-w-xs">
            Your current role doesn't grant access to any dashboard features. Contact your admin to update your access.
          </p>
        </div>
      )}
    </div>
  );
}
